import React from "react";
import { motion } from "framer-motion";
import cvData from "../data/cvData";
import "../components/Section.css";
import Seo from "../components/Seo";
import { fadeUp, staggerContainer, staggerItem, viewportOnce, easing } from "../utils/motionVariants";
import "./SkillsPage.css";

const iconFor = (name) => {
  const n = String(name || "").toLowerCase();
  const choose = (src) => (src ? <img src={src} alt="" /> : <span className="icon-placeholder">{name?.[0]}</span>);
  if (n.includes("react")) return choose("/assets/techstack/react-svgrepo-com.svg");
  if (n.includes("next")) return choose("/assets/techstack/next-js-svgrepo-com.svg");
  if (n === "html" || n.includes("html5")) return choose("/assets/techstack/html-5-svgrepo-com.svg");
  if (n === "css" || n.includes("css3")) return choose("/assets/techstack/css-3-svgrepo-com.svg");
  if (n.includes("javascript")) return choose("/assets/techstack/javascript-svgrepo-com.svg");
  if (n.includes("bootstrap")) return choose("/assets/techstack/bootstrap-svgrepo-com.svg");
  if (n.includes("material ui")) return choose("/assets/techstack/material-ui.svg");
  if (n.includes("laravel")) return choose("/assets/techstack/laravel-svgrepo-com.svg");
  if (n.includes("node")) return choose("/assets/techstack/node-js-svgrepo-com.svg");
  if (n.includes("flutter")) return choose("/assets/techstack/flutter-svgrepo-com.svg");
  if (n.includes("dart")) return choose("/assets/techstack/dart-svgrepo-com.svg");
  if (n.includes("mysql")) return choose("/assets/techstack/mysql-logo-svgrepo-com.svg");
  if (n.includes("mongo")) return choose("/assets/techstack/mongodb-logo-svgrepo-com.svg");
  if (n.includes("astra")) return choose("/assets/techstack/datastax.svg");
  if (n.includes("firebase")) return choose("/assets/techstack/firebase-svgrepo-com.svg");
  if (n.includes("aws")) return choose("/assets/techstack/aws-svgrepo-com.svg");
  if (n.includes("gcp")) return choose("/assets/techstack/gcp-svgrepo-com.svg");
  if (n.includes("docker")) return choose("/assets/techstack/docker-svgrepo-com.svg");
  if (n.includes("python")) return choose("/assets/techstack/python-svgrepo-com.svg");
  if (n.includes("langflow")) return choose("/assets/techstack/langflow-icon.svg");
  if (n.includes("postman")) return choose("/assets/techstack/postman-icon-svgrepo-com.svg");
  if (n === "git") return choose("/assets/techstack/github-142-svgrepo-com.svg");
  if (n.includes("n8n")) return choose("/assets/techstack/n8n-icon.svg");
  return choose();
};

const groups = [
  { key: "frontend", title: "Frontend", tag: "tag-lilac", items: cvData.skills.frontend },
  { key: "backend", title: "Backend", tag: "tag-mint", items: cvData.skills.backend },
  { key: "mobile", title: "Mobile", tag: "tag-peach", items: cvData.skills.mobile },
  { key: "databases", title: "Databases", tag: "tag-mist", items: cvData.skills.databases },
  { key: "cloud", title: "Cloud & DevOps", tag: "tag-mint", items: cvData.skills.cloud },
  { key: "dataEngineering", title: "Data Engineering", tag: "tag-lilac", items: cvData.skills.dataEngineering },
  { key: "aiTools", title: "AI & Tools", tag: "tag-peach", items: cvData.skills.aiTools },
];

function SkillCard({ name }) {
  return (
    <motion.div
      className="expertise-card"
      variants={staggerItem}
      whileHover={{ y: -6, scale: 1.05, rotate: -1 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.25, ease: easing }}
    >
      <motion.div
        className="expertise-icon"
        whileHover={{ rotate: 12, scale: 1.15 }}
        transition={{ duration: 0.3 }}
      >
        {iconFor(name)}
      </motion.div>
      <div>
        <strong>{name}</strong>
      </div>
    </motion.div>
  );
}

export default function SkillsPage() {
  const totalSkills = groups.reduce((sum, group) => sum + group.items.length, 0);

  const skillSummary = Object.values(cvData.skills).flat().slice(0, 12).join(", ");

  return (
    <section className="section section-white skills-page">
      <Seo
        title="Skills"
        path="/skills"
        description={`Technical expertise across frontend, backend, mobile, cloud, and AI tooling - ${skillSummary} and more.`}
      />
      <motion.div
        className="skills-header"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <p className="eyebrow">Technical Expertise</p>
        <h2>Technical Expertise</h2>
        <p>
          Explore my capabilities organized by specialty. From delightful interfaces to scalable infrastructure,
          here’s a snapshot of the tools and stacks I ship with confidence.
        </p>
      </motion.div>

      <div className="expertise-groups">
        {groups.map((group, gIdx) => (
          <motion.div
            key={group.key}
            className="expertise-group"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
            custom={gIdx}
          >
            <motion.div
              className={`group-badge ${group.tag}`}
              variants={staggerItem}
              whileHover={{ scale: 1.04, rotate: -1 }}
              transition={{ duration: 0.25 }}
            >
              <span>{group.title}</span>
              <small>{group.items.length} skills</small>
            </motion.div>
            <motion.div
              className="expertise-grid"
              variants={staggerContainer}
            >
              {group.items.map((skill) => (
                <SkillCard key={skill} name={skill} />
              ))}
            </motion.div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="skills-overview panel"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={staggerContainer}
        whileHover={{ y: -4, boxShadow: "0 22px 50px rgba(15, 23, 42, 0.12)" }}
        transition={{ duration: 0.3, ease: easing }}
      >
        {[
          { value: totalSkills, label: "Total Skills" },
          { value: groups.length, label: "Categories" },
          { value: "2+", label: "Years Experience" },
        ].map((stat) => (
          <motion.div
            key={stat.label}
            variants={staggerItem}
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.25 }}
          >
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
